import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Sprout, KeyRound, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const tokenMissing = useMemo(() => token.length < 20, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F9F8F6]">
      <div
        className="hidden md:block relative"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1765370817530-346a87971414?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwxfHxjYWxtJTIwYWJzdHJhY3QlMjB3YXJtJTIwc2FuZCUyMHRleHR1cmV8ZW58MHx8fHwxNzc4MDgzMDIyfDA&ixlib=rb-4.1.0&q=85)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#1D3328]/40 via-transparent to-[#E07A5F]/20" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="label-kicker text-white/80">Almost there</p>
          <h2 className="font-display text-4xl font-medium mt-3 leading-tight">
            Choose<br />a new password.
          </h2>
          <p className="mt-4 text-sm text-white/80 max-w-sm">
            Pick something you&apos;ll remember. Mix letters, numbers and a few quiet years of debt repayment.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-md fade-in">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 rounded-xl bg-[#2C4A3B] flex items-center justify-center text-white">
              <Sprout className="w-5 h-5" strokeWidth={1.7} />
            </div>
            <div>
              <div className="font-display text-xl font-medium text-[#1D3328]">Quietus</div>
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#8B9389]">Debt Tracker</div>
            </div>
          </div>

          {done ? (
            <div className="space-y-5" data-testid="reset-success">
              <div className="w-12 h-12 rounded-2xl bg-[#E5EFE7] border border-[#829A88]/30 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-[#2C4A3B]" strokeWidth={1.8} />
              </div>
              <p className="label-kicker">All set</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A]">
                Password updated
              </h1>
              <p className="text-[#5C635A]">
                Redirecting you to sign in…
              </p>
              <Link
                to="/login"
                className="inline-block text-sm text-[#2C4A3B] font-medium underline underline-offset-4"
                data-testid="reset-success-login"
              >
                Sign in now →
              </Link>
            </div>
          ) : tokenMissing ? (
            <>
              <p className="label-kicker">Missing link</p>
              <h1 className="font-display text-4xl font-medium tracking-tight text-[#1A1C1A] mt-2">
                Invalid reset link
              </h1>
              <p className="mt-3 text-[#5C635A]">
                This link doesn&apos;t look right. It may have expired or been altered.
              </p>
              <Link
                to="/forgot-password"
                className="inline-block mt-6 text-[#2C4A3B] font-medium underline underline-offset-4"
                data-testid="reset-request-new"
              >
                Request a new link →
              </Link>
            </>
          ) : (
            <>
              <p className="label-kicker">Set new password</p>
              <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1A1C1A] mt-2">
                Choose a new password
              </h1>
              <p className="mt-3 text-[#5C635A]">
                At least 6 characters. You&apos;ll be signed back in after you set it.
              </p>

              <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="reset-form">
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1D3328] font-medium">New password</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8B9389]" />
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={6}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="rounded-xl border-[#E5E3DB] h-11 bg-white pl-9"
                      placeholder="At least 6 characters"
                      data-testid="reset-password-input"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm" className="text-[#1D3328] font-medium">Confirm password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    required
                    minLength={6}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="rounded-xl border-[#E5E3DB] h-11 bg-white"
                    placeholder="Type it again"
                    data-testid="reset-confirm-input"
                  />
                </div>
                {error && (
                  <div
                    className="text-sm text-[#B04A32] bg-[#F7E6DF] border border-[#E07A5F]/30 rounded-lg px-3 py-2"
                    data-testid="reset-error"
                  >
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 rounded-xl bg-[#2C4A3B] hover:bg-[#3A5F4C] text-white font-medium transition-all hover:-translate-y-0.5"
                  data-testid="reset-submit-button"
                >
                  {submitting ? "Updating…" : "Update password"}
                </Button>
              </form>

              <p className="mt-6 text-sm text-[#5C635A]">
                Changed your mind?{" "}
                <Link to="/login" className="text-[#2C4A3B] font-medium underline underline-offset-4" data-testid="reset-to-login">
                  Back to sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
